import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { idpLabel, type AdminOrgDetail, type AdminOrgMember, type Paginated, type UserStatusResult } from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  Input,
  LoadingPane,
  PageHeader,
  Table,
  formatDate,
  useToast,
} from '../../components/ui.js';
import './platform.css';

interface AdminOrg {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  memberCount: number;
  createdAt: string;
}

const PAGE_SIZE = 25;

export function ManageOrgsPage() {
  const { me, refresh } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isSystemAdmin = me?.user.isSystemAdmin ?? false;
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [memberToDisable, setMemberToDisable] = useState<AdminOrgMember | null>(null);

  const orgs = useQuery({
    queryKey: ['admin-orgs', offset, search],
    queryFn: () =>
      apiFetch<Paginated<AdminOrg>>('/admin/orgs', {
        query: { limit: PAGE_SIZE, offset, q: search || undefined },
      }),
    enabled: isSystemAdmin,
  });

  const detail = useQuery({
    queryKey: ['admin-org', selectedOrgId],
    queryFn: () => apiFetch<AdminOrgDetail>(`/admin/orgs/${selectedOrgId}`),
    enabled: isSystemAdmin && !!selectedOrgId,
  });

  const updateOrg = useMutation({
    mutationFn: ({ orgId, body }: { orgId: string; body: { plan?: string; status?: string } }) =>
      apiFetch<{ ok: true }>(`/admin/orgs/${orgId}`, { method: 'PATCH', body }),
    onSuccess: () => {
      toast('Organisation updated.', 'success');
      void queryClient.invalidateQueries({ queryKey: ['admin-org', selectedOrgId] });
      void queryClient.invalidateQueries({ queryKey: ['admin-orgs'] });
    },
    onError: (err) => toast(err instanceof ApiRequestError ? err.message : 'Update failed', 'error'),
  });

  const impersonate = useMutation({
    mutationFn: (userId: string) =>
      apiFetch<{ ok: true; user: { name: string } }>(`/admin/impersonate/${userId}`, { method: 'POST' }),
    onSuccess: async () => {
      // Rebuild the whole session context, then land on the user's dashboard.
      await refresh();
      queryClient.clear();
      window.location.href = '/';
    },
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Could not impersonate', 'error'),
  });

  const setUserStatus = useMutation({
    mutationFn: ({ userId, action }: { userId: string; action: 'disable' | 'enable' }) =>
      apiFetch<UserStatusResult>(`/admin/users/${userId}/${action}`, { method: 'POST' }),
    onSuccess: (result) => {
      toast(result.status === 'disabled' ? 'Account disabled.' : 'Account enabled.', 'success');
      setMemberToDisable(null);
      void queryClient.invalidateQueries({ queryKey: ['admin-org', selectedOrgId] });
    },
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Could not change account status', 'error'),
  });

  if (!isSystemAdmin) {
    return <EmptyState title="System administrators only" message="Platform organisation management." />;
  }
  if (orgs.isLoading) return <LoadingPane label="Loading organisations" />;

  return (
    <div>
      <PageHeader
        title="Manage organisations"
        subtitle="Every organisation on the platform, its members, plan and status"
      />

      <Card
        title="Organisations"
        actions={
          <Input
            aria-label="Search organisations"
            placeholder="Search name or slug…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOffset(0);
            }}
            style={{ maxWidth: 240 }}
          />
        }
      >
        <Table
          head={
            <>
              <th>Name</th>
              <th>Slug</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Members</th>
              <th>Created</th>
              <th />
            </>
          }
        >
          {(orgs.data?.items ?? []).map((o) => (
            <tr key={o.id}>
              <td style={{ fontWeight: 600 }}>{o.name}</td>
              <td>
                <code>{o.slug}</code>
              </td>
              <td>
                <Badge tone={o.plan === 'paid' ? 'success' : 'neutral'}>{o.plan}</Badge>
              </td>
              <td>
                <Badge tone={o.status === 'active' ? 'info' : 'danger'}>{o.status}</Badge>
              </td>
              <td>{o.memberCount}</td>
              <td style={{ whiteSpace: 'nowrap' }}>{formatDate(o.createdAt)}</td>
              <td>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setSelectedOrgId(selectedOrgId === o.id ? null : o.id)}
                >
                  {selectedOrgId === o.id ? 'Hide' : 'View'}
                </Button>
              </td>
            </tr>
          ))}
        </Table>
        {(orgs.data?.total ?? 0) > PAGE_SIZE && (
          <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
            <Button
              size="sm"
              variant="secondary"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={offset + PAGE_SIZE >= (orgs.data?.total ?? 0)}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        )}
      </Card>

      {selectedOrgId && (
        <Card title={detail.data ? detail.data.name : 'Loading…'}>
          {detail.isLoading || !detail.data ? (
            <LoadingPane label="Loading organisation" />
          ) : (
            <>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
                <Badge tone="neutral">{detail.data.slug}</Badge>
                {detail.data.domain && <Badge tone="info">{detail.data.domain}</Badge>}
                <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
                  {detail.data.memberCount} members · {detail.data.assessmentCount} assessments · created{' '}
                  {formatDate(detail.data.createdAt)}
                </span>
              </div>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    updateOrg.mutate({
                      orgId: detail.data!.id,
                      body: { plan: detail.data!.plan === 'paid' ? 'free' : 'paid' },
                    })
                  }
                >
                  {detail.data.plan === 'paid' ? 'Downgrade to free' : 'Upgrade to paid'}
                </Button>
                <Button
                  size="sm"
                  variant={detail.data.status === 'active' ? 'danger' : 'secondary'}
                  onClick={() => {
                    const next = detail.data!.status === 'active' ? 'suspended' : 'active';
                    if (
                      next === 'suspended' &&
                      !window.confirm(`Suspend ${detail.data!.name}? Members will lose access until reactivated.`)
                    ) {
                      return;
                    }
                    updateOrg.mutate({ orgId: detail.data!.id, body: { status: next } });
                  }}
                >
                  {detail.data.status === 'active' ? 'Suspend' : 'Reactivate'}
                </Button>
              </div>

              <Table
                head={
                  <>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Sign-in</th>
                    <th>Status</th>
                    <th>Last login</th>
                    <th />
                  </>
                }
              >
                {detail.data.members.map((m) => (
                  <tr key={m.userId}>
                    <td style={{ fontWeight: 600 }}>
                      {m.name}
                      {m.isSystemAdmin && (
                        <Badge tone="purple" >
                          platform admin
                        </Badge>
                      )}
                    </td>
                    <td>{m.email}</td>
                    <td>
                      <Badge tone="neutral">{m.role}</Badge>
                    </td>
                    <td>{idpLabel(m.idp)}</td>
                    <td>
                      <Badge tone={m.status === 'active' ? 'info' : 'danger'}>{m.status}</Badge>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {m.lastLoginAt ? formatDate(m.lastLoginAt) : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {!m.isSystemAdmin && m.userId !== me?.user.id && (
                          <Button
                            size="sm"
                            variant={m.status === 'active' ? 'danger' : 'secondary'}
                            disabled={setUserStatus.isPending}
                            onClick={() => {
                              if (m.status === 'active') setMemberToDisable(m);
                              else setUserStatus.mutate({ userId: m.userId, action: 'enable' });
                            }}
                          >
                            {m.status === 'active' ? 'Disable' : 'Enable'}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={
                            m.isSystemAdmin || m.userId === me?.user.id || m.status !== 'active' || impersonate.isPending
                          }
                          title={
                            m.isSystemAdmin
                              ? 'Cannot impersonate another platform admin'
                              : `View the app as ${m.name}`
                          }
                          onClick={() => {
                            if (window.confirm(`View the app as ${m.name} (${m.email})? All actions are audited.`)) {
                              impersonate.mutate(m.userId);
                            }
                          }}
                        >
                          Impersonate
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </Table>
            </>
          )}
        </Card>
      )}

      <Dialog
        open={!!memberToDisable}
        onClose={() => setMemberToDisable(null)}
        title="Disable account?"
      >
        <p>
          {memberToDisable?.name} ({memberToDisable?.email}) will be signed out of every device
          immediately and cannot sign in to any organisation until re-enabled. API tokens they
          created stop working. Their memberships, contributions and audit history are retained.
        </p>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setMemberToDisable(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={setUserStatus.isPending}
            onClick={() =>
              memberToDisable && setUserStatus.mutate({ userId: memberToDisable.userId, action: 'disable' })
            }
          >
            Disable account
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
